import { FC, useState } from 'react';
import { useTranslation } from '@packages/utils';
import { EIconNames, Icon } from 'components/Base/Icon';
import { useTheme } from 'styled-components';
import { useGetPeopleIdentityQuery } from 'redux/apiSlice/people.slice';
import { PersonOutline } from 'assets/icon/icon';
import { Flex } from 'antd';
import { Text } from 'components/Base/Typography';
import CreateCoordinatorModal from './CreateCoordinatorModal';
import { TCoordinatorProps } from './Coordinator.types';
import { TCreateCoordinatorModalOpen } from './CreateCoordinatorModal/CreateCoordinatorModal.types';
import CoordinatorSelector from './CoordinatorSelector';
import CoordinatorInfo from './CoordinatorInfo';
import {
  CoordinatorPlaceholder,
  CoordinatorTag,
  EditableSelect,
} from './Coordinator.styles';

const Coordinator: FC<TCoordinatorProps> = ({
  coordinators,
  id,
  isWorkshop = true,
  readonly = false,
  coordinatorType,
  label,
}) => {
  const resolvedType: NonNullable<TCoordinatorProps['coordinatorType']> =
    coordinatorType ?? (isWorkshop ? 'workshopSite' : 'eventSite');
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.workshops.details.basic-info.coordinator',
  });
  const theme = useTheme();

  const { data: peopleIdentityOptions, isLoading } =
    useGetPeopleIdentityQuery();

  const [selectedCoordinators, setSelectedCoordinators] = useState<any[]>([]);
  const [open, setOpen] = useState<TCreateCoordinatorModalOpen>({
    visible: false,
    initialName: '',
  });
  const [isEditingCoordinator, setIsEditingCoordinator] =
    useState<boolean>(false);

  const uniqueCoordinators = (coordinators ?? []).filter(
    (coordinator, index, arr) =>
      arr.findIndex(
        c =>
          (c?.id && coordinator?.id && c.id === coordinator.id) ||
          (!c?.id && !coordinator?.id && c?.fullName === coordinator?.fullName),
      ) === index,
  );

  return (
    <>
      <Flex
        align="center"
        data-cy="event-coordinator-input"
        gap={isWorkshop ? 12 : 4}
        style={{ maxWidth: 800 }}
      >
        <Flex align="center" gap={8} style={{ width: 160 }}>
          <PersonOutline size={22} />
          <Text color="#808080">{label ?? 'Coordinators'}</Text>
        </Flex>

        {readonly && (
          <div>
            {uniqueCoordinators.length ? (
              <>
                {uniqueCoordinators.map(coordinator => (
                  <CoordinatorInfo
                    key={coordinator?.id || coordinator?.fullName}
                    display={!!coordinator?.id}
                    popoverTitle={coordinator?.fullName}
                    email={coordinator?.email}
                    phone={coordinator?.phone}
                  >
                    <CoordinatorTag>{coordinator.fullName}</CoordinatorTag>
                  </CoordinatorInfo>
                ))}
              </>
            ) : (
              <CoordinatorPlaceholder>N/A</CoordinatorPlaceholder>
            )}
          </div>
        )}

        {!readonly && (
          <>
            {isEditingCoordinator ? (
              <CoordinatorSelector
                id={id}
                coordinators={coordinators}
                options={peopleIdentityOptions}
                selectedCoordinators={selectedCoordinators}
                setSelectedCoordinators={setSelectedCoordinators}
                setOpen={setOpen}
                isLoading={isLoading}
                onBlur={setIsEditingCoordinator}
                coordinatorType={resolvedType}
              />
            ) : (
              <EditableSelect onClick={() => setIsEditingCoordinator(true)}>
                {uniqueCoordinators.length ? (
                  uniqueCoordinators.map(coordinator => (
                    <CoordinatorInfo
                      key={coordinator?.id || coordinator?.fullName}
                      display={!!coordinator?.id}
                      popoverTitle={coordinator?.fullName}
                      email={coordinator?.email}
                      phone={coordinator?.phone}
                    >
                      <CoordinatorTag>{coordinator.fullName}</CoordinatorTag>
                    </CoordinatorInfo>
                  ))
                ) : (
                  <span>Select Coordinator</span>
                )}
                <Icon
                  icon={EIconNames.EDIT_OUTLINED}
                  size={20}
                  color={theme.colorBlackIconColor}
                />
              </EditableSelect>
            )}
          </>
        )}
      </Flex>
      <CreateCoordinatorModal
        open={open}
        setOpen={setOpen}
        label={t('MODAL_LABEL')}
      />
    </>
  );
};

export default Coordinator;
